/*
 * Copyright (c) 2017 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */
import {TSAuthEvent} from '../../models/enums/TSAuthEvent';
import {TSVersicherungsbeitragStatus} from '../../models/enums/TSVersicherungsbeitragStatus';
import TSBeitrag from '../../models/TSBeitrag';
import TSVersicherungsbeitrag from '../../models/TSVersicherungsbeitrag';
import ErrorService from '../errors/service/ErrorService';
import BeitragRS from './BeitragRS';
import VersicherungsbeitragRS from './versicherungsbeitragRS.rest';
import ILogService = angular.ILogService;
import IPromise = angular.IPromise;
import IQService = angular.IQService;
import IRootScopeService = angular.IRootScopeService;

export default class VersicherungsbeitragModelManager {
    private versicherungsbeitrag?: TSVersicherungsbeitrag;

    static $inject = ['VersicherungsbeitragRS', '$log', '$q', 'CONSTANTS', '$rootScope', 'BeitragRS', 'ErrorService'];

    /* @ngInject */
    constructor(private versicherungsbeitragRS: VersicherungsbeitragRS, private log: ILogService,
                private $q: IQService, private CONSTANTS: any,
                private $rootScope: IRootScopeService, private beitragRS: BeitragRS, private errorService: ErrorService) {

        $rootScope.$on(TSAuthEvent[TSAuthEvent.LOGOUT_SUCCESS], () => {
            this.setVersicherungsbeitrag(undefined);
            this.log.debug('Cleared Versicherungsbeitrag on logout');
        });
    }

    public initNewVersicherungsbeitrag(): IPromise<TSVersicherungsbeitrag> {
        this.versicherungsbeitrag = new TSVersicherungsbeitrag().init();
        return this.$q.when(this.versicherungsbeitrag);
    }

    /**
     * Je nach dem welche Rolle der Benutzer hat, wird das Gesuch aus der DB anders geholt.
     * Fuer Institutionen z.B. wird das Gesuch nur mit den relevanten Daten geholt
     */
    public openVersicherungsbeitrag(versicherungsbeitragId: string): IPromise<TSVersicherungsbeitrag> {
        return this.versicherungsbeitragRS.findVersicherungsbeitrag(versicherungsbeitragId)
            .then((response: TSVersicherungsbeitrag) => {
                this.setVersicherungsbeitrag(response);
                return response;
            });

    }

    public setVersicherungsbeitrag(versicherungsbeitrag?: TSVersicherungsbeitrag): void {
        this.versicherungsbeitrag = versicherungsbeitrag;
    }

    public getVersicherungsbeitrag(): TSVersicherungsbeitrag | undefined {
        return this.versicherungsbeitrag;
    }

    /**
     *
     * @param versicherungsbeitragId id die geprueft wird
     * @return {boolean} True wenn der aktuelle Versicherungsbeitrag die gegebene id hat
     */
    public isVersicherungsbeitrag(versicherungsbeitragId: string): boolean {
        if (this.versicherungsbeitrag) {
            return this.versicherungsbeitrag.id === versicherungsbeitragId;
        }
        return false;
    }

    public reloadVersicherungsbeitrag(): IPromise<TSVersicherungsbeitrag | undefined> {
        if (this.versicherungsbeitrag) {
            return this.versicherungsbeitragRS.findVersicherungsbeitrag(this.versicherungsbeitrag.id).then((versicherungsbeitragResp: any) => {
                this.setVersicherungsbeitrag(versicherungsbeitragResp);
                return this.versicherungsbeitrag;
            });
        }
        return this.$q.when(this.versicherungsbeitrag);
    }

    /**
     * Update das Gesuch
     * @returns {IPromise<TSGesuch>}
     */
    public updateVersicherungsbeitrag(): IPromise<TSVersicherungsbeitrag> {
        if (this.versicherungsbeitrag) {
            return this.versicherungsbeitragRS.update(this.versicherungsbeitrag).then((gesuchResponse: TSVersicherungsbeitrag) => {
                this.versicherungsbeitrag = gesuchResponse;
                return this.versicherungsbeitrag;
            });
        } else {
            throw 'ILLEGAL_STATE';
        }
    }

    /**
     * Returns true if the Versicherungsbeitrag has the given status
     * @param status
     * @returns {boolean}
     */
    public isVersicherungsbeitragStatus(status: TSVersicherungsbeitragStatus): boolean {
        if (this.versicherungsbeitrag) {
            return this.versicherungsbeitrag.status === status;
        }
        return false;

    }

    public clearVersicherungsbeitrag(): void {
        this.versicherungsbeitrag = undefined;
    }

    public saveVersicherungsbeitrag(versicherungsbeitrag: TSVersicherungsbeitrag): IPromise<TSVersicherungsbeitrag> {
        return this.versicherungsbeitragRS.update(versicherungsbeitrag).then((schadResp: TSVersicherungsbeitrag) => {
            this.versicherungsbeitrag = schadResp;
            return this.versicherungsbeitrag;
        });
    }



    public deleteBeitrag(): IPromise<TSVersicherungsbeitrag> {
        if (this.versicherungsbeitrag) {
            return this.versicherungsbeitragRS.deleteBeitrag(this.versicherungsbeitrag.id).then((response: any) => {
                return this.clearBeitrag();
            });
        }
        this.log.error('could not delete Beitrag because no versicherungsbeitrag is managed');
        this.errorService.addMesageAsError('ERROR_UNEXPECTED');
        return this.$q.when(new TSVersicherungsbeitrag());
    }

    public clearBeitrag(): TSVersicherungsbeitrag {
        if (this.versicherungsbeitrag) {
            this.versicherungsbeitrag.beitrag = undefined;
            this.versicherungsbeitrag.status = TSVersicherungsbeitragStatus.IN_BEARBEITUNG_FS;
            return this.versicherungsbeitrag;
        }
        return new TSVersicherungsbeitrag;
    }

    public saveVersicherungsbeitragBeitrag(beitragToSave: TSBeitrag): IPromise<TSBeitrag> {
        if (this.versicherungsbeitrag) {

            return this.beitragRS.saveVersicherungsbeitragBeitrag(this.versicherungsbeitrag.id, beitragToSave)
                .then((storedBeitrag: TSBeitrag) => {
                    if (this.versicherungsbeitrag) {
                        this.versicherungsbeitrag.beitrag = storedBeitrag;
                        this.versicherungsbeitrag.status = TSVersicherungsbeitragStatus.AUSZAHLUNG;
                    }
                    return storedBeitrag;
                });
        }
        this.log.error('could not save Beitrag because no versicherungsbeitrag is managed');
        this.errorService.addMesageAsError('ERROR_UNEXPECTED');
        return this.$q.when(beitragToSave);
    }

}



// WEBPACK FOOTER //
// ./src/app/core/service/VersicherungsbeitragModelManager.ts